<template>
  <div>
    <data-table
      ref="datatableRef"
      :config="datatableConfig"
      :endpoint="endpoint"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @edit-click="handleEditClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import DataTable from '../../grid/DataTable.vue';
import { imageColumn, franchiseColumn, priceColumn } from '../../grid/productTableColumns';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface ProductRow {
  so_product_track_stock?: number | boolean;
  so_product_franchise_id?: number | null;
  [key: string]: unknown;
}

const emit = defineEmits<{
  edit: [id: Id];
  delete: [id: Id];
}>();

// Get i18n composable
const { t } = useI18n();

// Reactive data
const datatableRef = ref<DataTableInstance | null>(null);

// Computed properties
const endpoint = computed<string>(() => '/index.php/so/products/lista');

const tableColumns = computed(() => {
  return [
    {
      data: 'so_product_id',
      title: t('ID'),
      width: 130,
      sortable: true,
      filterType: 'text',
      renderType: 'clickable_id',
      mergedColumns: [
        franchiseColumn(t, 'so_product_franchise_name')
      ]
    },
    imageColumn(t),
    { data: 'so_product_name', title: t('Nome'), width: 200, sortable: true, filterType: 'text' },
    {
      data: 'so_product_category_name',
      title: t('Categoria'),
      width: 150,
      sortable: true,
      filterType: 'text'
    },
    {
      data: 'so_product_sku',
      title: t('SKU'),
      width: 120,
      sortable: true,
      filterType: 'text'
    },
    priceColumn(t, 'so_product_default_price', t('Preço Padrão'), 130),
    {
      data: 'so_product_unit_of_measure',
      title: t('Unidade'),
      width: 100,
      sortable: true
    },
    {
      data: 'so_product_track_stock',
      title: t('Controla Estoque'),
      width: 120,
      sortable: true,
      renderType: 'badge',
      badgeConfig: (row: ProductRow) => ({
        label: row.so_product_track_stock ? t('Sim') : t('Não'),
        severity: row.so_product_track_stock ? 'success' : 'secondary',
        tooltip: row.so_product_track_stock ? t('Controla estoque') : t('Não controla estoque')
      })
    },
    {
      data: 'so_product_franchise_id',
      title: t('Escopo'),
      width: 110,
      sortable: true,
      renderType: 'badge',
      badgeConfig: (row: ProductRow) => ({
        label: row.so_product_franchise_id ? t('Franquia') : t('Global'),
        severity: row.so_product_franchise_id ? 'info' : 'success'
      })
    }
  ];
});

const datatableConfig = computed(() => {
  return {
    filename: `so_products_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pageLength: 25,
    pagination: true,
    rowSelection: 'single',
    sortable: true,
    resizableColumns: true,
    showFilters: true,
    filterDisplay: 'row',
    stripedRows: true,
    showGridlines: true,
    size: 'small'
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

const csvButtonConfig = computed(() => {
  return {
    show: true,
    text: t('CSV')
  };
});

// Methods
const loadData = (): void => {
  const stack = new Error().stack;
  log('SOProductsTable', 'loadData', 'Triggering table refresh', {
    callStack: stack?.split('\n').slice(1, 4).join('\n')
  });
  if (datatableRef.value) {
    datatableRef.value.loadData();
  }
};

const handleDataLoaded = (data: unknown[]): void => {
  log('SOProductsTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  eventBus.emit('globalMessage.success', t('Carregados') + ` ${data.length} ` + t('registros de produtos'));
};

const handleDataError = (error: { message?: string } | Error): void => {
  log('SOProductsTable', 'handleDataError', 'Error loading data', error);
  const message = error instanceof Error ? error.message : (error.message || '');
  eventBus.emit('globalMessage.error', t('Erro ao carregar produtos') + ': ' + message);
};

const handleEditClick = (productId: Id): void => {
  log('SOProductsTable', 'handleEditClick', 'Edit clicked for ID', productId);
  emit('edit', productId);
};

const handleRefresh = (): void => {
  log('SOProductsTable', 'event:so-stock.adjusted', 'Received');
  loadData();
};

// Lifecycle
onMounted(() => {
  log('SOProductsTable', 'mounted', 'Setting up event listeners');

  // Don't load data on mount - let DataTable handle initial load via autoLoad
  log('SOProductsTable', 'mounted', 'Initial data load will be handled by DataTable autoLoad');

  eventBus.on('so-stock.adjusted', handleRefresh);
});

onBeforeUnmount(() => {
  log('SOProductsTable', 'beforeUnmount', 'Cleaning up event listeners');

  // Clean up event listeners
  eventBus.off('so-stock.adjusted', handleRefresh);
});

// Expose methods for parent components
defineExpose({
  loadData
});
</script>

<style scoped>
/* Add any component-specific styles here */
</style>
